import React, { useEffect, useState, useCallback, useRef } from "react";
import { Helmet } from "react-helmet-async";
import {
  Container,
  Typography,
  Grid,
  Card,
  Box,
  TextField,
  InputAdornment,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Divider,
  Stack,
  MenuItem,
  Select,
  InputLabel,
  Alert,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Button,
  IconButton,
  Switch,
  Tooltip,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import toast from "react-hot-toast";
import Iconify from "../../../components/iconify";
import FullScreenLoader from "../../../components/full-screen-loader";
import { GET, PUT } from "../../../config/https";
import APIS from "../../../config/api";
import Color from "../../../theme/color";

export interface CustomChargeItem {
  id: string;
  name: string;
  type: "FixedPerTicket" | "FixedPerOrder" | "Percentage";
  value: number;
  isVisible: boolean; // Show to Customer (itemized) vs Hide (bundled)
  isEnabled: boolean; // Active vs Paused
  description?: string;
}

export default function ServiceFeeSettings() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [events, setEvents] = useState<any[]>([]);
  const [eventsLoading, setEventsLoading] = useState<boolean>(false);
  const [onlyActivePublished, setOnlyActivePublished] = useState<boolean>(true);
  const [selectedEventObj, setSelectedEventObj] = useState<any | null>(null);
  const [eventSearchInput, setEventSearchInput] = useState<string>("");
  const searchTimerRef = useRef<any>(null);
  const [allFeeRules, setAllFeeRules] = useState<any[]>([]);

  // Form State
  const [selectedScope, setSelectedScope] = useState<string>("global"); // "global" | "event"
  const [selectedEventId, setSelectedEventId] = useState<number | "">("");
  const [feeType, setFeeType] = useState<number>(2); // 1 = Percentage, 2 = FixedAmount
  const [feeValue, setFeeValue] = useState<number>(2.0);
  const [ccServiceFeePercentage, setCcServiceFeePercentage] = useState<number>(3.4);
  const [ccServiceFeeFlat, setCcServiceFeeFlat] = useState<number>(0.3);
  const [taxPercentage, setTaxPercentage] = useState<number>(8.25);
  const [minFeeAmount, setMinFeeAmount] = useState<string>("");
  const [maxFeeAmount, setMaxFeeAmount] = useState<string>("");
  const [reason, setReason] = useState<string>("Administrative update");

  // Friendly UI Customization State
  const [baseFeeDisplayName, setBaseFeeDisplayName] = useState<string>("Service Fee");
  const [showBaseFeeRate, setShowBaseFeeRate] = useState<boolean>(false);
  const [baseFeeIsVisible, setBaseFeeIsVisible] = useState<boolean>(true);

  const [ccFeeDisplayName, setCcFeeDisplayName] = useState<string>("Payment Processing");
  const [showCcFeeRate, setShowCcFeeRate] = useState<boolean>(false);
  const [ccFeeIsVisible, setCcFeeIsVisible] = useState<boolean>(true);

  const [taxDisplayName, setTaxDisplayName] = useState<string>("Tax");
  const [showTaxRate, setShowTaxRate] = useState<boolean>(true);

  // Dynamic Custom Charge Sections State
  const [customCharges, setCustomCharges] = useState<CustomChargeItem[]>([]);

  // Live Simulator state
  const [simSubTotal, setSimSubTotal] = useState<number>(35.0);
  const [simTicketQty, setSimTicketQty] = useState<number>(1);

  useEffect(() => {
    fetchEvents("", onlyActivePublished);
    fetchCurrentFee();
    fetchAllFees();
  }, []);

  const fetchEvents = useCallback(async (query = "", activeOnly = true) => {
    setEventsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("PageSize", "30");
      params.append("PageNumber", "0");
      if (query.trim()) {
        params.append("SearchValue", query.trim());
      }
      if (activeOnly) {
        params.append("IsPublish", "true");
        params.append("ActiveOnly", "true");
      }
      const res: any = await GET(`${APIS.GET_EVENT_DETIAL_ADMIN}?${params.toString()}`);
      if (res?.isSuccess && res?.result) {
        setEvents(res.result);
      }
    } catch (e) {
      console.error("Error fetching events", e);
    } finally {
      setEventsLoading(false);
    }
  }, []);

  const handleSearchInputChange = (newQuery: string) => {
    setEventSearchInput(newQuery);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      fetchEvents(newQuery, onlyActivePublished);
    }, 350);
  };

  const handleToggleActivePublished = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setOnlyActivePublished(checked);
    fetchEvents(eventSearchInput, checked);
  };

  const parseFeeType = (val: any): number => {
    if (typeof val === "string") {
      if (val.toLowerCase().includes("percent")) return 1;
      if (val.toLowerCase().includes("fixed")) return 2;
    }
    return Number(val) === 1 ? 1 : 2;
  };

  const fetchCurrentFee = async (eventId?: number) => {
    setLoading(true);
    try {
      const url = eventId ? `${APIS.GET_SERVICE_FEE}?eventId=${eventId}` : APIS.GET_SERVICE_FEE;
      const res: any = await GET(url);
      if (res?.isSuccess && res?.result) {
        const data = res.result;
        setFeeType(parseFeeType(data.feeType));
        setFeeValue(data.feeValue ?? 2.0);
        setCcServiceFeePercentage(data.ccServiceFeePercentage ?? 3.4);
        setCcServiceFeeFlat(data.ccServiceFeeFlat ?? 0.3);
        setTaxPercentage(data.taxPercentage ?? 8.25);
        setMinFeeAmount(data.minFeeAmount !== null && data.minFeeAmount !== undefined ? data.minFeeAmount.toString() : "");
        setMaxFeeAmount(data.maxFeeAmount !== null && data.maxFeeAmount !== undefined ? data.maxFeeAmount.toString() : "");

        setBaseFeeDisplayName(data.baseFeeDisplayName || "Service Fee");
        setShowBaseFeeRate(Boolean(data.showBaseFeeRate));
        setBaseFeeIsVisible(data.baseFeeIsVisible !== false);

        setCcFeeDisplayName(data.ccFeeDisplayName || "Payment Processing");
        setShowCcFeeRate(Boolean(data.showCcFeeRate));
        setCcFeeIsVisible(data.ccFeeIsVisible !== false);

        setTaxDisplayName(data.taxDisplayName || "Tax");
        setShowTaxRate(data.showTaxRate !== false);

        if (data.customChargesJson) {
          try {
            const parsed = JSON.parse(data.customChargesJson);
            if (Array.isArray(parsed)) {
              setCustomCharges(parsed);
            } else if (parsed && Array.isArray(parsed.charges)) {
              setCustomCharges(parsed.charges);
              if (parsed.baseFeeDisplayName) setBaseFeeDisplayName(parsed.baseFeeDisplayName);
              if (parsed.showBaseFeeRate !== undefined) setShowBaseFeeRate(Boolean(parsed.showBaseFeeRate));
              if (parsed.baseFeeIsVisible !== undefined) setBaseFeeIsVisible(Boolean(parsed.baseFeeIsVisible));
              if (parsed.ccFeeDisplayName) setCcFeeDisplayName(parsed.ccFeeDisplayName);
              if (parsed.showCcFeeRate !== undefined) setShowCcFeeRate(Boolean(parsed.showCcFeeRate));
              if (parsed.ccFeeIsVisible !== undefined) setCcFeeIsVisible(Boolean(parsed.ccFeeIsVisible));
              if (parsed.taxDisplayName) setTaxDisplayName(parsed.taxDisplayName);
              if (parsed.showTaxRate !== undefined) setShowTaxRate(Boolean(parsed.showTaxRate));
            } else {
              setCustomCharges([]);
            }
          } catch {
            setCustomCharges([]);
          }
        } else {
          setCustomCharges([]);
        }
      }
    } catch (e) {
      console.error("Error fetching fee config", e);
      toast.error("Failed to load fee configuration");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllFees = async () => {
    try {
      const res: any = await GET(APIS.GET_ALL_SERVICE_FEES);
      if (res?.isSuccess && res?.result) {
        setAllFeeRules(res.result);
      }
    } catch (e) {
      console.error("Error fetching fee rules", e);
    }
  };

  const handleScopeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSelectedScope(val);
    if (val === "global") {
      setSelectedEventId("");
      setSelectedEventObj(null);
      fetchCurrentFee();
    } else {
      fetchEvents("", onlyActivePublished);
    }
  };

  const handleEventAutocompleteChange = (newValue: any | null) => {
    setSelectedEventObj(newValue);
    const evId = newValue ? Number(newValue.id) : "";
    setSelectedEventId(evId);
    if (evId) {
      fetchCurrentFee(Number(evId));
    } else {
      fetchCurrentFee();
    }
  };

  // Custom Charges Handlers
  const handleAddCustomCharge = () => {
    const newItem: CustomChargeItem = {
      id: `charge_${Date.now()}`,
      name: `Charge Section #${customCharges.length + 1}`,
      type: "FixedPerTicket",
      value: 1.0,
      isVisible: true,
      isEnabled: true,
      description: "",
    };
    setCustomCharges((prev) => [...prev, newItem]);
    toast.success("New charge section added");
  };

  const handleUpdateCustomCharge = (id: string, field: keyof CustomChargeItem, val: any) => {
    setCustomCharges((prev) =>
      prev.map((c) => (c.id === id ? { ...c, [field]: val } : c))
    );
  };

  const handleRemoveCustomCharge = (id: string) => {
    setCustomCharges((prev) => prev.filter((c) => c.id !== id));
    toast("Charge section removed", { icon: "🗑️" });
  };

  const handleToggleVisibility = (id: string) => {
    setCustomCharges((prev) =>
      prev.map((c) => (c.id === id ? { ...c, isVisible: !c.isVisible } : c))
    );
  };

  const handleToggleEnabled = (id: string) => {
    setCustomCharges((prev) =>
      prev.map((c) => (c.id === id ? { ...c, isEnabled: !c.isEnabled } : c))
    );
  };

  const handleSave = async () => {
    if (feeValue < 0) {
      toast.error("Base fee value cannot be negative");
      return;
    }
    if (feeType === 1 && feeValue > 100) {
      toast.error("Percentage fee cannot exceed 100%");
      return;
    }
    if (ccServiceFeePercentage < 0 || ccServiceFeePercentage > 100) {
      toast.error("Credit card percentage must be between 0% and 100%");
      return;
    }
    if (taxPercentage < 0 || taxPercentage > 100) {
      toast.error("Tax percentage must be between 0% and 100%");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        eventId: selectedScope === "event" && selectedEventId ? Number(selectedEventId) : null,
        feeType: parseFeeType(feeType),
        feeValue: Number(feeValue),
        ccServiceFeePercentage: Number(ccServiceFeePercentage),
        ccServiceFeeFlat: Number(ccServiceFeeFlat),
        taxPercentage: Number(taxPercentage),
        minFeeAmount: minFeeAmount !== "" ? Number(minFeeAmount) : null,
        maxFeeAmount: maxFeeAmount !== "" ? Number(maxFeeAmount) : null,
        isActive: true,
        reason: reason || "Admin fee configuration update",
        baseFeeDisplayName: baseFeeDisplayName || "Service Fee",
        showBaseFeeRate: Boolean(showBaseFeeRate),
        baseFeeIsVisible: Boolean(baseFeeIsVisible),
        ccFeeDisplayName: ccFeeDisplayName || "Payment Processing",
        showCcFeeRate: Boolean(showCcFeeRate),
        ccFeeIsVisible: Boolean(ccFeeIsVisible),
        taxDisplayName: taxDisplayName || "Tax",
        showTaxRate: Boolean(showTaxRate),
        customChargesJson: JSON.stringify(customCharges),
      };

      const res: any = await PUT(APIS.UPDATE_SERVICE_FEE, payload);
      if (res?.isSuccess) {
        toast.success("Service fee settings updated successfully!");
        fetchAllFees();
      } else {
        toast.error(res?.message || "Failed to update service fee settings");
      }
    } catch (e: any) {
      toast.error(e?.message || "Error saving fee settings");
    } finally {
      setSaving(false);
    }
  };

  const handleResetDefaults = () => {
    setFeeType(2);
    setFeeValue(2.0);
    setCcServiceFeePercentage(3.4);
    setCcServiceFeeFlat(0.3);
    setTaxPercentage(8.25);
    setMinFeeAmount("");
    setMaxFeeAmount("");
    setBaseFeeDisplayName("Service Fee");
    setShowBaseFeeRate(false);
    setBaseFeeIsVisible(true);
    setCcFeeDisplayName("Payment Processing");
    setShowCcFeeRate(false);
    setCcFeeIsVisible(true);
    setTaxDisplayName("Tax");
    setShowTaxRate(true);
    setCustomCharges([]);
    setReason("Reset to platform default settings ($2.00 fee, 3.4% + $0.30 CC, 8.25% Tax)");
    toast("Default parameters populated. Click 'Save Fee Configuration' to commit.", { icon: "ℹ️" });
  };

  // Live Simulator calculation (100% matches PaymentService.cs)
  const calculateSimulation = () => {
    const sub = Number(simSubTotal) || 0;
    const qty = Number(simTicketQty) || 1;

    let baseService = 0;
    if (feeType === 1) {
      baseService = (sub * feeValue) / 100;
    } else {
      baseService = qty * feeValue;
    }

    const minCap = minFeeAmount !== "" ? Number(minFeeAmount) : null;
    const maxCap = maxFeeAmount !== "" ? Number(maxFeeAmount) : null;

    if (minCap !== null && baseService < minCap) baseService = minCap;
    if (maxCap !== null && baseService > maxCap) baseService = maxCap;

    let customChargesTotal = 0;
    const customBreakdown: Array<{
      id: string;
      name: string;
      amount: number;
      type: string;
      rate: number;
      isVisible: boolean;
      isEnabled: boolean;
    }> = [];

    customCharges.forEach((c) => {
      if (c.isEnabled) {
        let chargeAmt = 0;
        if (c.type === "FixedPerTicket") {
          chargeAmt = qty * Number(c.value);
        } else if (c.type === "FixedPerOrder") {
          chargeAmt = Number(c.value);
        } else if (c.type === "Percentage") {
          chargeAmt = (sub * Number(c.value)) / 100;
        }
        chargeAmt = Math.round(chargeAmt * 100) / 100;
        customChargesTotal += chargeAmt;
        customBreakdown.push({
          id: c.id,
          name: c.name || "Custom Charge",
          amount: chargeAmt,
          type: c.type,
          rate: Number(c.value),
          isVisible: c.isVisible,
          isEnabled: c.isEnabled,
        });
      }
    });

    const totalBeforeTax = sub + baseService + customChargesTotal;
    const tax = Math.round(((totalBeforeTax * taxPercentage) / 100) * 100) / 100;
    const totalWithTax = totalBeforeTax + tax;

    const cc = totalWithTax > 0 ? Math.round((((totalWithTax * ccServiceFeePercentage) / 100) + Number(ccServiceFeeFlat)) * 100) / 100 : 0;
    const totalServiceFees = Math.round((baseService + customChargesTotal + cc) * 100) / 100;
    const grandTotal = Math.round((totalWithTax + cc) * 100) / 100;

    return {
      subTotal: sub.toFixed(2),
      baseService: baseService.toFixed(2),
      customChargesTotal: customChargesTotal.toFixed(2),
      customBreakdown,
      tax: tax.toFixed(2),
      ccService: cc.toFixed(2),
      totalServiceFees: totalServiceFees.toFixed(2),
      grandTotal: grandTotal.toFixed(2),
    };
  };

  const simResult = calculateSimulation();

  return (
    <>
      <Helmet>
        <title>Service Fee Management | EventPrix Admin</title>
      </Helmet>

      <FullScreenLoader open={loading} />

      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Page Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={4}>
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Platform Service Fees & Charges
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Dynamically customize charge sections, toggle customer visibility (show/hide), and configure platform rates.
            </Typography>
          </Box>

          <LoadingButton
            variant="outlined"
            color="primary"
            startIcon={<Iconify icon="carbon:reset" />}
            onClick={handleResetDefaults}
          >
            Reset to Defaults
          </LoadingButton>
        </Stack>

        {/* Live Overview Cards */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 2.5, bgcolor: "background.neutral", boxShadow: 1 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 1.5,
                    bgcolor: "primary.lighter",
                    color: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Iconify icon="solar:ticket-bold" width={28} />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Base Ticket Fee
                  </Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {feeType === 1 ? `${feeValue}%` : `$${Number(feeValue).toFixed(2)}`}
                    <Typography component="span" variant="caption" color="text.secondary" ml={0.5}>
                      {feeType === 1 ? "of price" : "per ticket"}
                    </Typography>
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 2.5, bgcolor: "background.neutral", boxShadow: 1 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 1.5,
                    bgcolor: "info.lighter",
                    color: "info.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Iconify icon="solar:card-bold" width={28} />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Credit Card Fee
                  </Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {ccServiceFeePercentage}% + ${Number(ccServiceFeeFlat).toFixed(2)}
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 2.5, bgcolor: "background.neutral", boxShadow: 1 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 1.5,
                    bgcolor: "warning.lighter",
                    color: "warning.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Iconify icon="solar:bill-list-bold" width={28} />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Sales Tax Rate
                  </Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {taxPercentage}%
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 2.5, bgcolor: "background.neutral", boxShadow: 1 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 1.5,
                    bgcolor: "success.lighter",
                    color: "success.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Iconify icon="solar:settings-bold" width={28} />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Custom Charges
                  </Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {customCharges.filter((c) => c.isEnabled).length} Active Sections
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>
        </Grid>

        {/* Main Configuration Grid */}
        <Grid container spacing={3}>
          {/* Form Column */}
          <Grid item xs={12} md={7}>
            <Card sx={{ p: 3, boxShadow: 2 }}>
              <Typography variant="h6" fontWeight={700} mb={2}>
                Fee & Rate Configuration
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Stack spacing={3}>
                {/* Scope selection */}
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
                    Apply Configuration To:
                  </FormLabel>
                  <RadioGroup row value={selectedScope} onChange={handleScopeChange}>
                    <FormControlLabel
                      value="global"
                      control={<Radio />}
                      label="All Events (Global Platform Default)"
                    />
                    <FormControlLabel
                      value="event"
                      control={<Radio />}
                      label="Specific Event Override"
                    />
                  </RadioGroup>
                </FormControl>

                {selectedScope === "event" && (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      bgcolor: "background.neutral",
                      border: "1px solid",
                      borderColor: "primary.light",
                    }}
                  >
                    <Stack spacing={2}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                        <Typography variant="subtitle2" fontWeight={700}>
                          Select Event to Override:
                        </Typography>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={onlyActivePublished}
                              onChange={handleToggleActivePublished}
                              color="primary"
                            />
                          }
                          label={
                            <Typography variant="caption" fontWeight={600} color={onlyActivePublished ? "primary.main" : "text.secondary"}>
                              Prioritize Active & Published Only
                            </Typography>
                          }
                        />
                      </Box>

                      <Autocomplete
                        fullWidth
                        size="small"
                        options={events}
                        loading={eventsLoading}
                        value={selectedEventObj}
                        onChange={(_e, newValue) => handleEventAutocompleteChange(newValue)}
                        inputValue={eventSearchInput}
                        onInputChange={(_e, newInputValue, reason) => {
                          if (reason === "input" || reason === "clear") {
                            handleSearchInputChange(newInputValue);
                          }
                        }}
                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                        getOptionLabel={(option) => {
                          if (!option) return "";
                          return option.title ? `${option.title} (#${option.id})` : `Event #${option.id}`;
                        }}
                        filterOptions={(x) => x}
                        ListboxProps={{
                          style: {
                            maxHeight: 280,
                            overflowY: "auto",
                          },
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Search by event title or ID (e.g. Austin, 18610)..."
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Iconify icon="eva:search-fill" sx={{ color: "text.disabled", width: 20, height: 20 }} />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <>
                                  {eventsLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        renderOption={(props, option) => {
                          const isUpcoming = option.endDateUtc ? new Date(option.endDateUtc) >= new Date() : true;
                          return (
                            <Box
                              component="li"
                              {...props}
                              key={option.id}
                              sx={{
                                py: 1.2,
                                px: 1.5,
                                borderBottom: "1px solid",
                                borderColor: "divider",
                                "&:last-child": { borderBottom: "none" },
                              }}
                            >
                              <Box sx={{ width: "100%" }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                                  <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ maxWidth: 320 }}>
                                    {option.title || `Event #${option.id}`}
                                  </Typography>
                                  <Stack direction="row" spacing={0.5}>
                                    <Chip
                                      size="small"
                                      label={option.isPublish ? "Published" : "Draft"}
                                      color={option.isPublish ? "success" : "default"}
                                      sx={{ height: 20, fontSize: 10, fontWeight: 700 }}
                                    />
                                    <Chip
                                      size="small"
                                      label={isUpcoming ? "Active" : "Past"}
                                      color={isUpcoming ? "info" : "default"}
                                      variant="outlined"
                                      sx={{ height: 20, fontSize: 10 }}
                                    />
                                  </Stack>
                                </Stack>
                                <Stack direction="row" spacing={2} sx={{ mt: 0.5, color: "text.secondary", fontSize: 11 }}>
                                  <span>ID: #{option.id}</span>
                                  {option.startDate && (
                                    <span>Start: {new Date(option.startDate).toLocaleDateString()}</span>
                                  )}
                                  {option.endDate && (
                                    <span>End: {new Date(option.endDate).toLocaleDateString()}</span>
                                  )}
                                </Stack>
                              </Box>
                            </Box>
                          );
                        }}
                        noOptionsText={
                          eventsLoading ? "Searching events..." : "No events found matching your criteria"
                        }
                      />

                      {selectedEventObj && (
                        <Card
                          sx={{
                            p: 1.5,
                            bgcolor: "primary.lighter",
                            border: "1px solid",
                            borderColor: "primary.main",
                            borderRadius: 1.5,
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Box>
                              <Typography variant="caption" color="primary.dark" fontWeight={700} textTransform="uppercase">
                                Selected Event Override
                              </Typography>
                              <Typography variant="subtitle2" fontWeight={700} color="primary.dark">
                                {selectedEventObj.title} (ID #{selectedEventObj.id})
                              </Typography>
                            </Box>
                            <Button
                              size="small"
                              color="error"
                              variant="outlined"
                              onClick={() => {
                                setSelectedEventObj(null);
                                setSelectedEventId("");
                                setEventSearchInput("");
                                fetchCurrentFee();
                              }}
                              sx={{ fontSize: 11, py: 0.2 }}
                            >
                              Clear Override
                            </Button>
                          </Stack>
                        </Card>
                      )}
                    </Stack>
                  </Paper>
                )}

                {/* Base Service Fee Type */}
                <Box>
                  <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
                    Base Ticket Service Fee Type:
                  </FormLabel>
                  <RadioGroup
                    row
                    value={feeType}
                    onChange={(e) => setFeeType(Number(e.target.value))}
                  >
                    <FormControlLabel
                      value={2}
                      control={<Radio />}
                      label="Fixed Amount ($ per ticket)"
                    />
                    <FormControlLabel
                      value={1}
                      control={<Radio />}
                      label="Percentage (% of ticket price)"
                    />
                  </RadioGroup>
                </Box>

                {/* Base Fee Value */}
                <TextField
                  fullWidth
                  label={feeType === 1 ? "Base Service Fee Percentage" : "Base Service Fee Amount"}
                  type="number"
                  inputProps={{ step: "0.01", min: "0" }}
                  value={feeValue}
                  onChange={(e) => setFeeValue(Number(e.target.value))}
                  InputProps={{
                    startAdornment: feeType === 2 ? <InputAdornment position="start">$</InputAdornment> : null,
                    endAdornment: feeType === 1 ? <InputAdornment position="end">%</InputAdornment> : null,
                  }}
                  helperText={
                    feeType === 2
                      ? "Charged per paid ticket (e.g. $2.00). Free tickets are bypassed."
                      : "Percentage calculated on ticket subtotal."
                  }
                />

                {/* Base Fee UI Customization */}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Checkout Display Label"
                      value={baseFeeDisplayName}
                      onChange={(e) => setBaseFeeDisplayName(e.target.value)}
                      placeholder="e.g. Service Fee"
                      helperText="Name shown to customer on checkout order summary"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} display="flex" alignItems="center">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={showBaseFeeRate}
                          onChange={(e) => setShowBaseFeeRate(e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <Typography variant="body2">
                          Show rate details (e.g. {feeType === 1 ? `(${feeValue}%)` : `($${Number(feeValue).toFixed(2)}/ticket)`})
                        </Typography>
                      }
                    />
                  </Grid>
                </Grid>

                {/* Optional Min/Max Caps */}
                {feeType === 1 && (
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Min Fee Cap ($)"
                        type="number"
                        placeholder="Optional (e.g. 1.00)"
                        value={minFeeAmount}
                        onChange={(e) => setMinFeeAmount(e.target.value)}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Max Fee Cap ($)"
                        type="number"
                        placeholder="Optional (e.g. 25.00)"
                        value={maxFeeAmount}
                        onChange={(e) => setMaxFeeAmount(e.target.value)}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                      />
                    </Grid>
                  </Grid>
                )}

                {/* Credit Card Processing Fee */}
                <Typography variant="subtitle2" color="primary.main" fontWeight={700} sx={{ pt: 1 }}>
                  Credit Card & Payment Processing
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Credit Card Fee (%)"
                      type="number"
                      inputProps={{ step: "0.01", min: "0", max: "100" }}
                      value={ccServiceFeePercentage}
                      onChange={(e) => setCcServiceFeePercentage(Number(e.target.value))}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      }}
                      helperText="Standard rate (e.g. 3.4%)"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Flat CC Fee ($)"
                      type="number"
                      inputProps={{ step: "0.01", min: "0" }}
                      value={ccServiceFeeFlat}
                      onChange={(e) => setCcServiceFeeFlat(Number(e.target.value))}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      helperText="Fixed per transaction (e.g. $0.30)"
                    />
                  </Grid>
                </Grid>

                {/* Credit Card Fee UI Customization */}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Checkout Display Label"
                      value={ccFeeDisplayName}
                      onChange={(e) => setCcFeeDisplayName(e.target.value)}
                      placeholder="e.g. Payment Processing"
                      helperText="Name shown to customer on checkout"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} display="flex" alignItems="center">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={showCcFeeRate}
                          onChange={(e) => setShowCcFeeRate(e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <Typography variant="body2">
                          Show rate details (e.g. ({ccServiceFeePercentage}% + ${Number(ccServiceFeeFlat).toFixed(2)}))
                        </Typography>
                      }
                    />
                  </Grid>
                </Grid>

                {/* Sales Tax */}
                <Typography variant="subtitle2" color="primary.main" fontWeight={700} sx={{ pt: 1 }}>
                  Sales Tax
                </Typography>
                <TextField
                  fullWidth
                  label="Sales Tax Rate (%)"
                  type="number"
                  inputProps={{ step: "0.01", min: "0", max: "100" }}
                  value={taxPercentage}
                  onChange={(e) => setTaxPercentage(Number(e.target.value))}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  helperText="Platform tax rate applied to subtotal + base fee (e.g. 8.25%)"
                />

                {/* Sales Tax UI Customization */}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Checkout Display Label"
                      value={taxDisplayName}
                      onChange={(e) => setTaxDisplayName(e.target.value)}
                      placeholder="e.g. Tax"
                      helperText="Tax name shown on customer checkout"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} display="flex" alignItems="center">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={showTaxRate}
                          onChange={(e) => setShowTaxRate(e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <Typography variant="body2">
                          Show tax rate % (e.g. ({taxPercentage}%))
                        </Typography>
                      }
                    />
                  </Grid>
                </Grid>

                {/* Dynamic Custom Charge Sections */}
                <Box sx={{ pt: 2 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                    <Box>
                      <Typography variant="subtitle2" color="primary.main" fontWeight={700}>
                        Additional Custom Charge Sections
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Add manual charge sections (Facility Fee, Booking Fee, etc.), configure their calculation type, and choose whether to Show them on checkout or Hide/Bundle them.
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Iconify icon="solar:add-circle-bold" />}
                      onClick={handleAddCustomCharge}
                      sx={{ bgcolor: Color.primary, "&:hover": { bgcolor: Color.primary } }}
                    >
                      Add Charge Section
                    </Button>
                  </Stack>

                  {customCharges.length === 0 ? (
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 3,
                        textAlign: "center",
                        bgcolor: "background.neutral",
                        borderStyle: "dashed",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No custom charge sections added yet. Click <strong>"Add Charge Section"</strong> to create manual fees like Booking Fee, Facility Surcharge, or Technology Fee.
                      </Typography>
                    </Paper>
                  ) : (
                    <Stack spacing={2}>
                      {customCharges.map((charge, idx) => (
                        <Paper
                          key={charge.id}
                          variant="outlined"
                          sx={{
                            p: 2.5,
                            bgcolor: charge.isEnabled ? "background.paper" : "action.hover",
                            borderColor: charge.isEnabled ? "divider" : "action.disabledBackground",
                            borderRadius: 1.5,
                          }}
                        >
                          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Typography variant="subtitle2" fontWeight={700}>
                                #{idx + 1} {charge.name || "Untitled Charge"}
                              </Typography>
                              <Chip
                                size="small"
                                label={charge.isVisible ? "Visible to Customer" : "Hidden / Bundled"}
                                color={charge.isVisible ? "success" : "default"}
                                variant={charge.isVisible ? "filled" : "outlined"}
                              />
                              {!charge.isEnabled && (
                                <Chip size="small" label="Paused" color="warning" variant="outlined" />
                              )}
                            </Stack>

                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <Tooltip title={charge.isVisible ? "Visible on customer checkout (Itemized)" : "Hidden from customer (Bundled into Service Fee)"}>
                                <IconButton
                                  size="small"
                                  color={charge.isVisible ? "primary" : "default"}
                                  onClick={() => handleToggleVisibility(charge.id)}
                                >
                                  <Iconify icon={charge.isVisible ? "solar:eye-bold" : "solar:eye-closed-bold"} width={20} />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="Delete Charge Section">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemoveCustomCharge(charge.id)}
                                >
                                  <Iconify icon="solar:trash-bin-trash-bold" width={20} />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </Stack>

                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={5}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Section Name"
                                value={charge.name}
                                onChange={(e) => handleUpdateCustomCharge(charge.id, "name", e.target.value)}
                                placeholder="e.g. Facility Surcharge"
                              />
                            </Grid>

                            <Grid item xs={12} sm={4}>
                              <FormControl fullWidth size="small">
                                <InputLabel id={`charge-type-label-${charge.id}`}>Charge Type</InputLabel>
                                <Select
                                  labelId={`charge-type-label-${charge.id}`}
                                  value={charge.type}
                                  label="Charge Type"
                                  onChange={(e) => handleUpdateCustomCharge(charge.id, "type", e.target.value)}
                                >
                                  <MenuItem value="FixedPerTicket">$ Fixed per ticket</MenuItem>
                                  <MenuItem value="FixedPerOrder">$ Fixed per order</MenuItem>
                                  <MenuItem value="Percentage">% of subtotal</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={3}>
                              <TextField
                                fullWidth
                                size="small"
                                label={charge.type === "Percentage" ? "Rate (%)" : "Amount ($)"}
                                type="number"
                                inputProps={{ step: "0.01", min: "0" }}
                                value={charge.value}
                                onChange={(e) => handleUpdateCustomCharge(charge.id, "value", Number(e.target.value))}
                                InputProps={{
                                  startAdornment: charge.type !== "Percentage" ? <InputAdornment position="start">$</InputAdornment> : null,
                                  endAdornment: charge.type === "Percentage" ? <InputAdornment position="end">%</InputAdornment> : null,
                                }}
                              />
                            </Grid>
                          </Grid>

                          <Stack direction="row" alignItems="center" justifyContent="space-between" mt={1.5} pt={1} borderTop="1px dashed" borderColor="divider">
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={charge.isVisible}
                                  onChange={() => handleToggleVisibility(charge.id)}
                                />
                              }
                              label={
                                <Typography variant="caption" color="text.secondary">
                                  {charge.isVisible ? "Show on Checkout (Itemized)" : "Hide from Customer (Bundled)"}
                                </Typography>
                              }
                            />

                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  color="success"
                                  checked={charge.isEnabled}
                                  onChange={() => handleToggleEnabled(charge.id)}
                                />
                              }
                              label={
                                <Typography variant="caption" color="text.secondary">
                                  {charge.isEnabled ? "Active" : "Paused"}
                                </Typography>
                              }
                            />
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </Box>

                {/* Audit Reason */}
                <TextField
                  fullWidth
                  label="Update Reason / Audit Note"
                  placeholder="e.g. Added facility fee and updated rate adjustment"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  multiline
                  rows={2}
                />

                {/* Save Button */}
                <Box sx={{ pt: 2 }}>
                  <LoadingButton
                    fullWidth
                    size="large"
                    variant="contained"
                    loading={saving}
                    onClick={handleSave}
                    startIcon={<Iconify icon="solar:disk-bold" />}
                    sx={{
                      bgcolor: Color.primary,
                      "&:hover": { bgcolor: Color.primary },
                    }}
                  >
                    Save Fee Configuration
                  </LoadingButton>
                </Box>
              </Stack>
            </Card>
          </Grid>

          {/* Right Column: Live Simulator Calculator */}
          <Grid item xs={12} md={5}>
            <Card sx={{ p: 3, boxShadow: 2, bgcolor: "background.paper", position: "sticky", top: 80 }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <Iconify icon="solar:calculator-minimalistic-bold" width={24} color="primary.main" />
                <Typography variant="h6" fontWeight={700}>
                  Live Calculation Simulator
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Simulates real-time checkout calculations using all active form settings and custom charge sections:
              </Typography>

              <Grid container spacing={2} mb={3}>
                <Grid item xs={7}>
                  <TextField
                    fullWidth
                    label="Sample Subtotal ($)"
                    type="number"
                    value={simSubTotal}
                    onChange={(e) => setSimSubTotal(Number(e.target.value))}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={5}>
                  <TextField
                    fullWidth
                    label="Qty of Tickets"
                    type="number"
                    value={simTicketQty}
                    onChange={(e) => setSimTicketQty(Number(e.target.value))}
                  />
                </Grid>
              </Grid>

              {/* API Breakdown Box */}
              <Paper variant="outlined" sx={{ p: 2.5, bgcolor: "background.neutral", borderRadius: 1.5 }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase" mb={1.5} display="block">
                  Calculation Breakdown (API & Checkout Equivalent)
                </Typography>

                <Stack spacing={1.5}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Ticket SubTotal:
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      ${simResult.subTotal}
                    </Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {baseFeeDisplayName || "Service Fee"}{showBaseFeeRate ? ` (${feeType === 1 ? `${feeValue}%` : `$${Number(feeValue).toFixed(2)}/ticket`})` : ""}:
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={600}>
                      ${simResult.baseService}
                    </Typography>
                  </Stack>

                  {/* Custom Charges in Simulator */}
                  {simResult.customBreakdown.map((c) => (
                    <Stack key={c.id} direction="row" justifyContent="space-between" alignItems="center">
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {c.name}:
                        </Typography>
                        <Chip
                          size="small"
                          sx={{ height: 18, fontSize: 10 }}
                          label={c.isVisible ? "Visible" : "Bundled"}
                          color={c.isVisible ? "success" : "default"}
                        />
                      </Box>
                      <Typography variant="body2" fontWeight={600}>
                        ${c.amount.toFixed(2)}
                      </Typography>
                    </Stack>
                  ))}

                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      {taxDisplayName || "Tax"}{showTaxRate ? ` (${taxPercentage}%)` : ""}:
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      ${simResult.tax}
                    </Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      {ccFeeDisplayName || "Payment Processing"}{showCcFeeRate ? ` (${ccServiceFeePercentage}% + ${Number(ccServiceFeeFlat).toFixed(2)})` : ""}:
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color="info.main">
                      ${simResult.ccService}
                    </Typography>
                  </Stack>

                  <Divider />

                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" fontWeight={700}>
                      Total Service Fees:
                    </Typography>
                    <Typography variant="body2" fontWeight={700} color="warning.main">
                      ${simResult.totalServiceFees}
                    </Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between" sx={{ pt: 1 }}>
                    <Typography variant="subtitle1" fontWeight={800} color="primary.main">
                      Grand Total:
                    </Typography>
                    <Typography variant="subtitle1" fontWeight={800} color="primary.main">
                      ${simResult.grandTotal}
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>

              <Alert severity="info" sx={{ mt: 3 }}>
                Updates saved here are immediately reflected in{" "}
                <strong>/api/payment/charge-calculation</strong> and rendered transparently on customer checkout order summaries.
              </Alert>
            </Card>
          </Grid>
        </Grid>

        {/* Existing Configured Rules Table */}
        <Box mt={5}>
          <Typography variant="h6" fontWeight={700} mb={2}>
            Configured Fee Rules & History
          </Typography>
          <Card sx={{ boxShadow: 1 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Scope / Event</TableCell>
                  <TableCell>Base Fee</TableCell>
                  <TableCell>Custom Sections</TableCell>
                  <TableCell>Credit Card Fee</TableCell>
                  <TableCell>Tax %</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Updated (UTC)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allFeeRules.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary" py={2}>
                        No custom rules saved yet. Currently operating on system defaults ($2.00 / 3.4% / 8.25%).
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  allFeeRules.map((rule) => {
                    let customSectionsCount = 0;
                    if (rule.customChargesJson) {
                      try {
                        const parsed = JSON.parse(rule.customChargesJson);
                        if (Array.isArray(parsed)) customSectionsCount = parsed.length;
                      } catch {}
                    }
                    return (
                      <TableRow key={rule.id}>
                        <TableCell>
                          {rule.eventId ? (
                            <Chip label={`Event: ${rule.eventName || rule.eventId}`} size="small" color="secondary" />
                          ) : (
                            <Chip label="Global Platform Default" size="small" color="primary" />
                          )}
                        </TableCell>
                        <TableCell>
                          {rule.feeType === 1 ? `${rule.feeValue}%` : `$${Number(rule.feeValue).toFixed(2)}`}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={`${customSectionsCount} custom sections`}
                            size="small"
                            variant="outlined"
                            color={customSectionsCount > 0 ? "primary" : "default"}
                          />
                        </TableCell>
                        <TableCell>
                          {rule.ccServiceFeePercentage}% + ${Number(rule.ccServiceFeeFlat).toFixed(2)}
                        </TableCell>
                        <TableCell>{rule.taxPercentage}%</TableCell>
                        <TableCell>
                          <Chip
                            label={rule.isActive ? "Active" : "Inactive"}
                            size="small"
                            color={rule.isActive ? "success" : "default"}
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(rule.updatedAtUtc).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Card>
        </Box>
      </Container>
    </>
  );
}
